import { useEffect, useState } from "react"
import { fboApi } from "@/api/fbo-api.client"
import type { GetToken } from "@/api/ats-sync-api.client"
import type { ContactStatus } from "@/components/ats-fbo/types"
import { mapContactStatus } from "./use-worker-profiles"
import { env } from "@/lib/env"
import { logger } from "@/lib/logger"
import { workerProfiles as mockWorkerProfiles, mockCreatedEntities } from "@/components/ats-fbo/mock-data"
import type { UserProfileWorkerDto } from "@/api/fbo-api.types"

export interface ContactForEmail {
  contactId: string
  contactName: string
  contactEmail: string
  contactStatus: ContactStatus
}

export interface WorkerLandscape {
  contactsForEmail: ContactForEmail[]
  loading: boolean
}

/** Group profile DTOs by ContactId, returning one ContactForEmail per distinct contact. */
function groupByContact(profiles: UserProfileWorkerDto[], email: string): ContactForEmail[] {
  const seen = new Map<number, ContactForEmail>()
  for (const p of profiles) {
    if (!seen.has(p.ContactId)) {
      seen.set(p.ContactId, {
        contactId: String(p.ContactId),
        contactName: p.Contact?.FullName ?? "Unknown",
        contactEmail: p.PrimaryEmail ?? email,
        contactStatus: mapContactStatus(p.Contact?.UserStatusId ?? 1),
      })
    }
  }
  return [...seen.values()]
}

/**
 * Landscape check: determines how many distinct contacts exist for the given
 * ATS email. Returns 0, 1, or many ContactForEmail entries.
 */
export function useWorkerLandscape(email: string, getToken: GetToken, refreshKey: number = 0): WorkerLandscape {
  const [contactsForEmail, setContactsForEmail] = useState<ContactForEmail[]>([])
  const [loading, setLoading] = useState(true)

  const useMock = env.useMockData || !env.fboApiUrl

  useEffect(() => {
    if (!email) {
      setContactsForEmail([])
      setLoading(false)
      return
    }

    // Mock path: group local mock data by contactId. Computed inside the effect
    // so that a refreshKey bump picks up dynamically created entities (correction flow).
    if (useMock) {
      const allProfiles = [...mockWorkerProfiles, ...mockCreatedEntities.profiles]
      const matching = allProfiles.filter(
        (w) => w.email.toLowerCase() === email.toLowerCase()
      )
      const seen = new Map<string, ContactForEmail>()
      for (const w of matching) {
        if (!seen.has(w.contactId)) {
          seen.set(w.contactId, {
            contactId: w.contactId,
            contactName: w.name,
            contactEmail: w.email,
            contactStatus: w.contactStatus,
          })
        }
      }
      setContactsForEmail([...seen.values()])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    fboApi.searchWorkerProfilesByEmail(email, getToken)
      .then((profiles) => {
        if (cancelled) return
        setContactsForEmail(groupByContact(profiles, email))
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        logger.error(err)
        setContactsForEmail([])
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [email, getToken, useMock, refreshKey])

  return { contactsForEmail, loading }
}
